import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import * as Highcharts from 'highcharts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarkerCoordinate, MarkerType } from 'src/app/google-map/interface/google-map.interface';
import { OpportunityScannerState } from 'src/app/opportunity-scanner/opportunity-scanner.state';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import { PropertyState } from 'src/app/workspace/state/property.state';
import constants from 'src/constant.json';
import { environment } from 'src/environments/environment';
import { CmaService } from '../cma.service';
import { CmaState } from '../cma.state';
import { SchoolInfoDialogComponent } from '../school-info-dialog/school-info-dialog.component';

interface CustomPoint extends Highcharts.Point {
  address: any;
}
interface SoldChartPoint extends Highcharts.PointOptionsObject {
  streetAddress?: string;
}

@Component({
  selector: 'app-view-cma-comparables',
  templateUrl: './view-cma-comparables.component.html',
  styleUrls: ['./view-cma-comparables.component.scss'],
})
export class ViewCmaComparablesComponent implements OnInit {
  @ViewChild('tabGroup') tabGroup!: MatTabGroup;
  @ViewChild('paginator') paginator: any;
  listingImageBaseUrlAlt = environment.listingImageBaseUrlAlt;
  onDestroyNotifier$ = new Subject();
  activeStatusTabIndex: number = 0;
  activeViewTabIndex: number = 0;
  isScreenWidthLarge: boolean = false;
  cmaReportId;
  comparableInfo: Array<any> = [];
  all: number;
  active: number;
  sold: number;
  activeUc: number;
  pending: number;
  colorMapping: { [key: string]: string } = {
    sold: '#c0392b', // Red
    pending: '#f39c12', // Orange
    active: '#27ae60', // Green
  };
  reportType: string;
  originalComparableInfo: any[] = []; // Keep the original data
  comparableStats: any;
  comparableMapUrl: any;
  chartRef!: Highcharts.Chart; // Store the chart instance
  Highcharts: typeof Highcharts = Highcharts;
  updateFlag = false;
  lat;
  long;
  zoom = 15;
  pagedComparableInfo = [];
  pagedTableComparableInfo = [];
  averageListPrice: any;
  averageLotSizes: any;
  markerList: MarkerCoordinate[] = [];
  chartOptions: Highcharts.Options = {
    chart: { type: 'scatter' },
    title: { text: null },
    xAxis: {
      title: { text: 'Price per Square Feet' },
    },
    yAxis: {
      title: { text: 'Sold Price' },
      opposite: true,
      labels: {
        formatter: function () {
          const val = Number(this.value);
          if (val >= 1000000) {
            const formatted = val / 1000000;
            return (
              '$' +
              (formatted % 1 === 0
                ? Highcharts.numberFormat(formatted, 0, '.', ',')
                : Highcharts.numberFormat(formatted, 1, '.', ',')) +
              'M'
            );
          } else if (val >= 1000) {
            const formatted = val / 1000;
            if (formatted < 10) {
              return '$' + Highcharts.numberFormat(formatted, 1, '.', ',') + 'K';
            } else {
              return '$' + Highcharts.numberFormat(formatted, 0, '.', ',') + 'K';
            }
          }
        },
      },
    },
    tooltip: {
      useHTML: true, // Enables HTML formatting
      pointFormatter: function () {
        const point = this as CustomPoint;
        return `
              <b>Street Address:</b> ${point.address}<br>
              <b>Price / SqFt:</b> $${Highcharts.numberFormat(this.x, 0, '.', ',')}<br>
              <b>Sold Price:</b> $${Highcharts.numberFormat(this.y, 0, '.', ',')}
          `;
      },
    },
    series: [{ type: 'scatter', name: '', data: [], showInLegend: false }],
    credits: {
      enabled: false, // <-- disables the Highcharts watermark
    },
  };
  averageDaysOfMarket: number;
  defaultPlace: any;
  streetViewAvailableMap: { [key: string]: boolean } = {};

  soldChartOptions: Highcharts.Options = {
    chart: { type: 'scatter' },
    title: { text: null },
    xAxis: { title: { text: 'Days on Market' } },
    yAxis: {
      title: { text: 'Price ($)' },
      opposite: true,
      labels: {
        formatter: function () {
          const val = Number(this.value);
          if (val >= 1000000) {
            const formatted = val / 1000000;
            return (
              '$' +
              (formatted % 1 === 0
                ? Highcharts.numberFormat(formatted, 0, '.', ',')
                : Highcharts.numberFormat(formatted, 1, '.', ',')) +
              'M'
            );
          } else if (val >= 1000) {
            const formatted = val / 1000;
            if (formatted < 10) {
              return '$' + Highcharts.numberFormat(formatted, 1, '.', ',') + 'K';
            } else {
              return '$' + Highcharts.numberFormat(formatted, 0, '.', ',') + 'K';
            }
          }
        },
      },
    },
    //tooltip: { pointFormat: 'Days on Market: {point.x}, Price: ${point.y}' },
    tooltip: {
      useHTML: true, // Enables HTML formatting
      pointFormatter: function () {
        const point = this as SoldChartPoint;
        return `
              <b>Street Address:</b> ${point.streetAddress}<br>
              <b>Days on Market:</b> ${Highcharts.numberFormat(this.x, 0, '.', ',')}<br>
              <b>Price:</b> $${Highcharts.numberFormat(this.y, 0, '.', ',')}
          `;
      },
    },
    series: [{ type: 'scatter', name: '', data: [], showInLegend: false }],
    credits: {
      enabled: false, // <-- disables the Highcharts watermark
    },
  };

  constructor(
    public cmaState: CmaState,
    public cmaService: CmaService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    public dialog: MatDialog,
    public opScanState: OpportunityScannerState,
    public propertyState: PropertyState,
    private subscriptionService: SubscriptionPlanService,
    private activatedRoute: ActivatedRoute
  ) { }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.isScreenWidthLarge = window.innerWidth > 768;
    if (!this.isScreenWidthLarge) {
      this.activeViewTabIndex = 1;
      this.viewTabChanged(1);
    }
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res && res.id) {
        this.cmaReportId = res.id;
      }
    });
    this.cmaState.cmaReports.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        this.reportType = res?.reportType;
        this.comparableStats = res?.compReportData?.valueReport?.comparableStats;
        this.comparableMapUrl = res?.compReportData?.valueReport?.comparableMapUrl;
        this.originalComparableInfo = res?.compReportData?.valueReport?.selectedComps || [];
        this.lat = Number(res?.compReportData?.valueReport?.subject?.Latitude);
        this.long = Number(res?.compReportData?.valueReport?.subject?.Longitude);
        this.comparableInfo = [...this.originalComparableInfo];
        this.sortComparableInfo();
        this.all = this.originalComparableInfo?.length || 0;
        this.active = this.originalComparableInfo?.filter((item) => item.StandardStatus === 'Active').length || 0;
        this.sold = this.originalComparableInfo?.filter((item) => item.StandardStatus === 'Sold').length || 0;
        this.pending = this.originalComparableInfo?.filter((item) => item.StandardStatus === 'Pending').length || 0;
        this.activeUc =
          this.originalComparableInfo?.filter(
            (item) => item.StandardStatus === 'Active UC' || item.StandardStatus === 'Active Under Contract'
          ).length || 0;
        this.generateMarkerList(this.comparableInfo);
        this.graphValue(this.comparableInfo);
        this.updateSoldChart(this.comparableInfo);
        this.calculateAverageDaysOnMarket();
        this.setMapCenter();
        this.chunkArray(this.comparableInfo, this.comparableInfo.length);
        this.isScreenWidthLarge = window.innerWidth > 768;
        if (!this.isScreenWidthLarge) {
          this.activeViewTabIndex = 1;
          this.viewTabChanged(1);
        }
      }
    });
  }

  getSimilarityScoreCeiled(score: number | string): number {
    return Math.ceil(Number(score));
  }

  truncateDistance(value: number | string): string {
    if (value === null || value === undefined) return '--';

    const num = Number(value);
    if (isNaN(num)) return '--';

    return (Math.floor(num * 100) / 100).toFixed(2);
  }

  chunkArray(array, size) {
    let i = 1;
    array.forEach((element) => {
      element['index'] = i;
      i = i + 1;
    });
    this.opScanState.comparableList = array;
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  statusTabChanged(tabChangeEvent: MatTabChangeEvent) {
    this.activeStatusTabIndex = tabChangeEvent.index;

    switch (this.activeStatusTabIndex) {
      case 0:
        this.comparableInfo = [...this.originalComparableInfo];
        break;

      case 1:
        this.comparableInfo = this.originalComparableInfo.filter(
          (item) =>
            item.StandardStatus === 'Active' ||
            item.StandardStatus === 'Pending' ||
            item.StandardStatus === 'Active UC' ||
            item.StandardStatus === 'Active Under Contract'
        );
        break;

      case 2:
        this.comparableInfo = this.originalComparableInfo.filter((item) => item.StandardStatus === 'Sold');
        break;

      default:
        this.comparableInfo = [...this.originalComparableInfo];
    }

    if (this.activeViewTabIndex == 1 || this.activeViewTabIndex == 2) {
      let pageSize = this.activeViewTabIndex == 1 ? 8 : 10;
      this.updatePagedData(0, pageSize);
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    }
    this.generateMarkerList(this.comparableInfo);
    this.cdr.detectChanges();
    this.sortComparableInfo();
  }

  generateMarkerList(comparableList: any[]): void {
    this.markerList = [];

    if (comparableList && comparableList.length > 0) {
      this.markerList = comparableList.map((comparable, index) => ({
        lat: Number(comparable?.Latitude || 0),
        lng: Number(comparable?.Longitude || 0),
        label: (index + 1).toString(),
        type: this.getMarkerType(comparable.StandardStatus),
        isSelected: comparable.isSelected,
        payload: {
          id: index + 1,
        },
      }));
      this.cdr.detectChanges();
    }
  }

  setMapCenter() {
    if (this.markerList.length > 0) {
      const latitudes = this.markerList.map((marker) => Number(marker.lat));
      const longitudes = this.markerList.map((marker) => Number(marker.lng));
      // Ensure all values are numbers before performing operations
      const avgLat = latitudes.reduce((sum, lat) => sum + lat, 0) / latitudes.length;
      const avgLng = longitudes.reduce((sum, lng) => sum + lng, 0) / longitudes.length;
      // Set center location only if markers exist, else use default center
      this.lat = isNaN(avgLat) ? 38.917211 : avgLat;
      this.long = isNaN(avgLng) ? -77.054361 : avgLng;
      const latDiff = Math.max(...latitudes) - Math.min(...latitudes);
      const lngDiff = Math.max(...longitudes) - Math.min(...longitudes);
      const maxDiff = Math.max(latDiff, lngDiff);
      if (maxDiff < 0.01) this.zoom = 14;
      else if (maxDiff < 0.05) this.zoom = 12;
      else if (maxDiff < 0.1) this.zoom = 10;
      else if (maxDiff < 1) this.zoom = 8;
      else this.zoom = 6;
    } else {
      this.lat = 38.917211;
      this.long = -77.054361;
      this.zoom = 12;
    }
  }

  editCma() {
    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.editCma.key)) return;
    let queryParams: any = {
      id: this.cmaReportId,
    };
    this.router.navigate(['/cma/edit'], { queryParams: queryParams, state: { from: 'comp' } });
  }

  formatDistance(distance: any): string {
    const num = Number(distance);
    return isNaN(num) ? '--' : num.toFixed(2);
  }

  getPropertiesByStatus(status: string) {
    return this.comparableInfo?.filter((p) => p.StandardStatus === status) || [];
  }

  getMarkerType(status?: string): MarkerType {
    let markerType: MarkerType;
    switch (status) {
      case 'Sold':
        markerType = 'sold';
        break;

      case 'Pending':
        markerType = 'pending';
        break;

      case 'Active':
      case 'Active Under Contract':
        markerType = 'active';
        break;

      default:
        markerType = 'sold';
        break;
    }
    return markerType;
  }

  openSchoolInfo(school: any) {
    this.dialog.open(SchoolInfoDialogComponent, {
      data: school,
      width: '300px',
    });
  }

  sortComparableInfo() {
    if (this.comparableInfo && Array.isArray(this.comparableInfo)) {
      this.comparableInfo = [...this.comparableInfo].sort((a, b) => b.SimilarityScore - a.SimilarityScore);
    }
  }

  openPropertyDetails(property) {
    property['reportType'] = this.reportType;
    this.propertyState.propertyDetailsSheetInput = property;
  }

  viewTabChanged(index: number) {
    this.activeViewTabIndex = index;
    // this.tabGroup.selectedIndex = 0;
    if (this.activeViewTabIndex == 1 || this.activeViewTabIndex == 2) {
      let pageSize = this.activeViewTabIndex == 1 ? 8 : 10;
      this.updatePagedData(0, pageSize);
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    }
    this.cdr.detectChanges();
  }

  roundUp(value: number | null | undefined): number | string {
    if (value === null || value === undefined) {
      return '--';
    }
    return Math.ceil(value);
  }
  getCount(status: string): number {
    return this.comparableInfo.filter((property) => property.StandardStatus === status).length;
  }

  onPageChange(event: PageEvent) {
    const startIndex = event.pageIndex * event.pageSize;
    const endIndex = startIndex + event.pageSize;
    this.updatePagedData(startIndex, endIndex);
  }

  updatePagedData(startIndex: number, endIndex: number) {
    this.pagedComparableInfo = this.comparableInfo.slice(startIndex, endIndex);
  }

  chartLoaded(chart: Highcharts.Chart) {
    this.chartRef = chart;
  }

  graphValue(value) {
    const finalArray = value.map(
      ({ PriceperSquareFeet, ClosePrice, DaysOnMarket, ListPrice, StandardStatus, UnparsedAddress }) => ({
        PriceperSquareFeet,
        ClosePrice,
        ListPrice,
        DaysOnMarket,
        StandardStatus,
        UnparsedAddress,
      })
    );
    const formattedData = finalArray
      .filter((item) => item.PriceperSquareFeet !== undefined)
      .map((item) => ({
        x: item.PriceperSquareFeet,
        y: Number(item.ClosePrice),
        address: item.UnparsedAddress,
        marker: {
          radius: 10,
          fillColor: this.colorMapping[item.StandardStatus?.toLowerCase()] || '#ccc',
        },
      }));
    this.updateChartData(formattedData);
  }

  updateChartData(newData: any[]) {
    if (!this.chartRef || !this.chartRef?.series?.[0]) {
      console.warn('Chart not initialized yet!');
      return;
    }
    this.chartRef.series[0].setData(newData, true);
    (this.chartOptions.series![0] as Highcharts.SeriesScatterOptions).data = newData;
    this.updateFlag = true;
  }

  updateSoldChart(data: any[]) {
    const scatterData: Highcharts.PointOptionsObject[] = [];
    const lineData: Highcharts.PointOptionsObject[] = [];
    data.forEach((item) => {
      if (item.DaysOnMarket && item.ListPrice && item.OriginalListPrice) {
        const day = Number(item.DaysOnMarket);
        const listPrice = Number(item.ListPrice);
        const originalListPrice = Number(item.OriginalListPrice);
        const fillColor = this.colorMapping[item.StandardStatus?.toLowerCase()] || '#ccc';

        scatterData.push({
          x: day,
          y: listPrice,
          streetAddress: item.UnparsedAddress,
          marker: { radius: 10, fillColor },
        } as unknown as SoldChartPoint);
        scatterData.push({
          x: day,
          y: originalListPrice,
          streetAddress: item.UnparsedAddress,
          marker: { radius: 10, fillColor },
        } as unknown as SoldChartPoint);
        lineData.push({
          x: day,
          y: listPrice,
          streetAddress: item.UnparsedAddress,
          color: fillColor,
        } as unknown as SoldChartPoint);
        lineData.push({
          x: day,
          y: originalListPrice,
          streetAddress: item.UnparsedAddress,
          color: fillColor,
        } as unknown as SoldChartPoint);
        lineData.push({ x: null, y: null });
      }
    });
    this.soldChartOptions = {
      ...this.soldChartOptions,
      series: [
        {
          type: 'scatter',
          name: '',
          data: scatterData,
          showInLegend: false,
        } as Highcharts.SeriesScatterOptions,
        // {
        //   type: 'line',
        //   name: 'Price Range Line',
        //   data: lineData,
        //   lineWidth: 3,
        //   marker: { enabled: false },
        //   states: {
        //     hover: {
        //       enabled: false,
        //     },
        //   },
        //   enableMouseTracking: false,
        // } as Highcharts.SeriesLineOptions,
      ],
    };
  }

  formatCurrencyWithSuffix(value: number): string {
    if (value >= 1_000_000) {
      return '$' + Highcharts.numberFormat(value / 1_000_000, 1, '.', ',') + 'M';
    } else if (value >= 1_000) {
      return '$' + Highcharts.numberFormat(value / 1_000, 0, '.', ',') + 'K';
    } else {
      return '$' + Highcharts.numberFormat(value, 0, '.', ',');
    }
  }

  // calculateAverageDaysOnMarket() {
  //   const soldProperties = this.comparableInfo.filter((item) => item?.StandardStatus?.toLowerCase() === 'sold');
  //   if (soldProperties.length === 0) {
  //     return 0;
  //   }

  //   const totalDaysOnMarket = soldProperties.reduce((sum, item) => {
  //     const days = Number(item.DaysOnMarket) || 0;
  //     return sum + days;
  //   }, 0);
  //   this.averageDaysOfMarket = totalDaysOnMarket / soldProperties.length;

  //   const totalRatio = soldProperties.reduce((sum, item) => {
  //     const ratio = item.ClosePrice / item.ListPrice;
  //     return sum + (ratio || 0);
  //   }, 0);

  //   let averageLotSizes = soldProperties.reduce((sum, item) => {
  //     const days = Number(item.PriceperSquareFeet) || 0;
  //     return sum + days;
  //   }, 0);
  //   this.averageLotSizes = averageLotSizes / soldProperties.length;
  //   this.averageDaysOfMarket = totalDaysOnMarket / soldProperties.length;

  //   const averageRatio = totalRatio && soldProperties && soldProperties.length ? totalRatio / soldProperties.length : 0;
  //   this.averageListPrice = averageRatio ? averageRatio * 100 : 0;
  // }
  calculateAverageDaysOnMarket() {
    const soldProperties = this.comparableInfo.filter((item) => item?.StandardStatus?.toLowerCase() === 'sold');

    const soldCount = soldProperties.length;
    if (soldCount === 0) {
      this.averageDaysOfMarket = 0;
      this.averageLotSizes = 0;
      this.averageListPrice = 0;
      return;
    }

    // ✅ Total Days on Market
    const totalDaysOnMarket = soldProperties.reduce((sum, item) => {
      const days = Number(item.DaysOnMarket);
      return sum + (isFinite(days) ? days : 0);
    }, 0);

    // ✅ Total Ratio (ClosePrice / ListPrice)
    const totalRatio = soldProperties.reduce((sum, item) => {
      const closePrice = Number(item.ClosePrice);
      const listPrice = Number(item.ListPrice);
      const ratio = listPrice > 0 ? closePrice / listPrice : 0;
      return sum + (isFinite(ratio) ? ratio : 0);
    }, 0);

    // ✅ Total Price per Square Feet
    const totalPricePerSqFt = soldProperties.reduce((sum, item) => {
      const pricePerSqFt = Number(item.PriceperSquareFeet);
      return sum + (isFinite(pricePerSqFt) ? pricePerSqFt : 0);
    }, 0);

    // ✅ Compute averages
    this.averageDaysOfMarket = totalDaysOnMarket / soldCount;
    this.averageLotSizes = totalPricePerSqFt / soldCount;
    const averageRatio = totalRatio / soldCount;
    this.averageListPrice = isFinite(averageRatio) ? averageRatio * 100 : 0;
  }

  trackByProperty(index: number, property: any) {
    return property.PMXPropertyId;
  }

  onStreetViewAvailable(property, isAvailable: boolean) {
    property.showBirdEye = !isAvailable;
  }
}
